import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import TempStart from './TempStart';
import AppShell, { useShellUser } from '../components/AppShell';
import CheckInMap from '../components/CheckInMap';
import {
  apiErrorMessage,
  checkInClosesMs,
  checkInOpensMs,
  flattenScheduleEvents,
  formatEventDate,
  formatEventTime,
  formatWindowTime,
  hasFinalScore,
  isPastEvent,
  isPredictionOpen,
  isWithinCheckInWindow,
  normalizeMediaUrl,
  pickSchoolLogoUrl,
  predictionLabel,
  readPrediction,
  schoolIdOf,
  todayStamp,
} from '../utils/gameSchedule';
import '../styles/Games.css';

const MAX_SCORE = 999;

function TeamMark({ name, logoUrl }) {
  const uri = normalizeMediaUrl(logoUrl);
  return (
    <span className="games-team">
      {uri ? <img src={uri} alt="" /> : <span className="games-logo-fallback" aria-hidden="true" />}
      <strong>{name || ''}</strong>
    </span>
  );
}

function CheckMark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1" y="1" width="14" height="14" rx="3" fill="#34c759" />
      <path d="M4.2 8.2 6.7 10.6 11.8 5.4" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function EmptyBox() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="none" stroke="#b0b0b0" strokeWidth="1.4" />
    </svg>
  );
}

function PredictMark({ active }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke={active ? '#00c8ff' : '#b0b0b0'} strokeWidth="1.4" />
      <path d="M5 2.2v2.2M11 2.2v2.2M2 6.5h12" fill="none" stroke={active ? '#00c8ff' : '#b0b0b0'} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ShareMark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 2.2v7.2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M5.2 4.8 8 2.1l2.8 2.7" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.2 8.2v4.2a1.2 1.2 0 0 0 1.2 1.2h7.2a1.2 1.2 0 0 0 1.2-1.2V8.2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function shareText(event) {
  const place = event.is_home ? 'Home' : 'Away';
  const predicted = predictionLabel(event);
  return [
    `${event.opponent_name} vs ${event.home_name}`,
    `${formatEventTime(event.time)} · ${place} · ${formatEventDate(event.date)}`,
    predicted ? `Predicted ${predicted}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

async function shareGame(event) {
  const text = shareText(event);
  if (navigator.share) {
    try {
      await navigator.share({ title: 'HomeCrowd', text });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
  }
}

function readPosition() {
  if (!navigator.geolocation) {
    return Promise.reject(new Error('Could not get GPS. Allow location access and try again.'));
  }
  return new Promise((resolve, reject) => {
    let pending = 2;
    let denied = false;
    const succeed = (coords) => {
      if (pending < 0) return;
      pending = -1;
      resolve(coords);
    };
    const fail = (err) => {
      if (pending < 0) return;
      if (err && err.code === 1) denied = true;
      pending -= 1;
      if (pending > 0) return;
      reject(new Error(
        denied
          ? 'Could not get GPS. Allow location access and try again.'
          : 'Location is unavailable. Turn on Location Services for your browser and try again.'
      ));
    };
    [
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 10 * 60 * 1000 },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 },
    ].forEach((options) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => succeed({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
        fail,
        options
      );
    });
  });
}

function ScoreStepper({ value, onChange }) {
  return (
    <span className="games-stepper">
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))} disabled={value <= 0} aria-label="Decrease">
        −
      </button>
      <strong>{value}</strong>
      <button type="button" onClick={() => onChange(Math.min(MAX_SCORE, value + 1))} disabled={value >= MAX_SCORE} aria-label="Increase">
        +
      </button>
    </span>
  );
}

function PredictDialog({ event, onClose, onSaved }) {
  const scored = hasFinalScore(event);
  const existing = readPrediction(event);
  const viewing = !scored && !!(existing || event?.already_predicted);
  const composing = !scored && !viewing && isPredictionOpen(event);
  const viewScores = existing || { home: 0, away: 0 };
  const [scores, setScores] = useState({ home: 0, away: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(scored);
  const [hasMore, setHasMore] = useState(false);
  const [subtitle, setSubtitle] = useState('');

  useEffect(() => {
    if (!scored || !event?.id) return undefined;
    let cancelled = false;
    apiService
      .getScheduleEventPredictions(event.id, { offset: 0, limit: 50 })
      .then((payload) => {
        if (cancelled) return;
        const homeScore = Number.isFinite(Number(payload?.home_score))
          ? Number(payload.home_score)
          : Number(event.home_score);
        const awayScore = Number.isFinite(Number(payload?.away_score))
          ? Number(payload.away_score)
          : Number(event.away_score);
        setSubtitle(`${event.home_name} ${homeScore} · ${event.opponent_name} ${awayScore}`);
        setResults(payload?.results || []);
        setHasMore(!!payload?.has_more);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load prediction results'));
      })
      .finally(() => {
        if (!cancelled) setResultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event, scored]);

  const loadMore = () => {
    if (!hasMore || resultsLoading || !event?.id) return;
    setResultsLoading(true);
    apiService
      .getScheduleEventPredictions(event.id, { offset: (results || []).length, limit: 50 })
      .then((payload) => {
        const rows = payload?.results || [];
        setResults((prev) => (prev || []).concat(rows));
        setHasMore(!!payload?.has_more && rows.length > 0);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Could not load prediction results')))
      .finally(() => setResultsLoading(false));
  };

  const confirm = async () => {
    if (!event?.id || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await apiService.createScheduleEventPrediction(event.id, {
        home_score: scores.home,
        away_score: scores.away,
      });
      const prediction = res?.prediction || scores;
      onSaved({
        ...event,
        already_predicted: true,
        my_prediction: {
          home_score: Number(prediction.home_score),
          away_score: Number(prediction.away_score),
        },
      });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save prediction'));
      setSubmitting(false);
    }
  };

  const lockTime = formatEventTime(event?.time);
  const title = scored ? 'Prediction results' : viewing ? 'Your prediction' : 'Predict the score';
  const note = scored ? subtitle : viewing ? 'Submitted' : lockTime !== 'TBD' ? `Locks at ${lockTime}` : 'Locks at tipoff';

  return (
    <div className="games-modal" role="presentation" onClick={onClose}>
      <div className="games-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <p className="games-dialog-kicker">Prediction</p>
        <h2>{title}</h2>
        {note ? <p className="games-dialog-note">{note}</p> : null}
        {scored ? (
          <div className="games-results">
            {(results || []).map((row) => (
              <div key={`${row.user_id || row.rank}-${row.home_score}-${row.away_score}`} className={row.is_you ? 'is-you' : ''}>
                <span>{row.rank}</span>
                <strong>{row.is_you ? 'You' : row.display_name || row.name || 'User'}</strong>
                <em>
                  {Number.isFinite(Number(row.home_score)) && Number.isFinite(Number(row.away_score))
                    ? `${row.home_score}–${row.away_score}`
                    : ''}
                </em>
              </div>
            ))}
            {!resultsLoading && results && !results.length ? <p>No predictions yet</p> : null}
            {hasMore ? (
              <button type="button" onClick={loadMore}>
                Show more
              </button>
            ) : null}
          </div>
        ) : (
          <div className="games-predict-match">
            <span>
              <TeamMark name={event?.home_name} logoUrl={event?.home_logo} />
              {viewing ? (
                <strong className="games-readonly-score">{viewScores.home}</strong>
              ) : (
                <ScoreStepper value={scores.home} onChange={(home) => setScores((prev) => ({ ...prev, home }))} />
              )}
            </span>
            <span>
              <TeamMark name={event?.opponent_name} logoUrl={event?.opponent_logo} />
              {viewing ? (
                <strong className="games-readonly-score">{viewScores.away}</strong>
              ) : (
                <ScoreStepper value={scores.away} onChange={(away) => setScores((prev) => ({ ...prev, away }))} />
              )}
            </span>
          </div>
        )}
        {error ? <p className="games-dialog-error">{error}</p> : null}
        <div className="games-dialog-actions">
          {composing ? (
            <button type="button" className="games-btn is-ghost" onClick={onClose}>
              Cancel
            </button>
          ) : null}
          {composing ? (
            <button type="button" className="games-btn is-primary" onClick={confirm} disabled={submitting}>
              {submitting ? 'Saving...' : 'Confirm prediction'}
            </button>
          ) : (
            <button type="button" className="games-btn is-primary" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckInScreen({ event, mode, user, error, awarded, submitting, locateTask, onClose, onSubmit }) {
  const [userCoord, setUserCoord] = useState(null);
  const opensMs = checkInOpensMs(event);
  const closesMs = checkInClosesMs(event);
  const tooEarly = opensMs != null && Date.now() < opensMs;
  const logo = normalizeMediaUrl(pickSchoolLogoUrl(user));

  useEffect(() => {
    if (mode !== 'map' || !navigator.geolocation) return undefined;
    let cancelled = false;
    let watchId = null;
    const task = locateTask || readPosition();
    task
      .then((coords) => {
        if (cancelled || !coords) return;
        setUserCoord(coords);
        watchId = navigator.geolocation.watchPosition(
          (pos) => {
            if (cancelled) return;
            setUserCoord({
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            });
          },
          () => {},
          { enableHighAccuracy: false, timeout: 20000, maximumAge: 10000 }
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };
  }, [mode, locateTask]);

  return (
    <section className="games-checkin">
      <header className="games-checkin-bar">
        <button type="button" onClick={onClose} aria-label="Back">
          ‹
        </button>
        <h2>Check in</h2>
      </header>
      {mode === 'window' ? (
        <div className="games-checkin-window">
          <h3>{tooEarly ? "Check-in isn't open yet" : 'Check-in is closed'}</h3>
          <p>
            {tooEarly
              ? 'You can check in starting 15 minutes before kickoff. Come back during the window below.'
              : 'The check-in window for this game has ended.'}
          </p>
          <div className="games-window">
            <span>
              <em>Opens</em>
              <strong>{formatWindowTime(opensMs) || 'TBD'}</strong>
            </span>
            <span>
              <em>Closes</em>
              <strong>{formatWindowTime(closesMs) || 'TBD'}</strong>
            </span>
          </div>
        </div>
      ) : null}
      {mode === 'map' ? (
        <div className="games-checkin-stage">
          <CheckInMap
            event={event}
            userCoord={userCoord}
            onLocate={async () => {
              const coords = await readPosition();
              setUserCoord(coords);
              return coords;
            }}
          />
          {userCoord ? null : <p className="games-locating">Finding your location...</p>}
        </div>
      ) : null}
      {mode === 'success' ? (
        <div className="games-checkin-success">
          {logo ? <img src={logo} alt="" /> : <span className="games-logo-fallback" />}
          <h3>Checked in!</h3>
          <p>{`You earned +${awarded} pts for attending tonight's game.`}</p>
        </div>
      ) : null}
      {error ? <p className="games-dialog-error games-checkin-alert">{error}</p> : null}
      <footer className="games-checkin-footer">
        {mode === 'map' ? (
          <button
            type="button"
            className="games-btn is-primary"
            onClick={() => onSubmit(userCoord)}
            disabled={submitting || !!event.already_checked_in}
          >
            {event.already_checked_in
              ? "You're already checked in"
              : submitting
                ? 'Checking in...'
                : 'Check in'}
          </button>
        ) : (
          <button type="button" className="games-btn is-primary" onClick={onClose}>
            {mode === 'success' ? 'Done' : 'Back to games'}
          </button>
        )}
      </footer>
    </section>
  );
}

function formatFanCount(total) {
  const count = Number(total) || 0;
  if (count === 1) return '1 fan predicted';
  return `${count.toLocaleString('en-US')} fans predicted`;
}

function predictionScore(row) {
  const home = Number(row && row.home_score);
  const away = Number(row && row.away_score);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return '';
  return `${home}:${away}`;
}

function eventMeta(event) {
  const parts = [formatEventDate(event.date), event.is_home ? 'Home' : 'Away'];
  if (event.sport) parts.push(event.sport);
  return parts.filter(Boolean).join(' · ');
}

function RankMark({ rank }) {
  const tone = rank === 1 ? '#C4A46A' : rank === 2 ? '#C8C8C8' : rank === 3 ? '#A36A25' : null;
  if (!tone) return <span className="games-rank-num">{rank}</span>;
  return (
    <span className="games-medal">
      <svg viewBox="0 0 26 28" aria-hidden="true">
        <path d="M13 1.4L22.4 4.8v8.2c0 6.2-4.3 11.4-9.4 13.6C8 24.4 3.6 19.2 3.6 13V4.8L13 1.4z" fill={tone} />
      </svg>
      <strong>{rank}</strong>
    </span>
  );
}

function PersonMark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="5.2" r="2.4" fill="#b0b0b0" />
      <path d="M3.2 13.2c.6-2.4 2.4-3.6 4.8-3.6s4.2 1.2 4.8 3.6" fill="none" stroke="#b0b0b0" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function PredictionBoard({ event }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setExpanded(false);
    setError('');
    apiService
      .getScheduleEventPredictions(event.id, { offset: 0, limit: 50 })
      .then((payload) => {
        if (cancelled) return;
        setRows(payload?.results || []);
        setTotal(Number(payload?.total) || 0);
        setHasMore(!!payload?.has_more);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not load prediction results'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event.id]);

  const viewAll = async () => {
    setExpanded(true);
    let next = rows;
    let more = hasMore;
    while (more) {
      try {
        const payload = await apiService.getScheduleEventPredictions(event.id, {
          offset: next.length,
          limit: 50,
        });
        const page = payload?.results || [];
        next = next.concat(page);
        more = !!payload?.has_more && page.length > 0;
        setRows(next);
        setTotal(Number(payload?.total) || next.length);
        setHasMore(more);
        if (!page.length) break;
      } catch (err) {
        setError(apiErrorMessage(err, 'Could not load prediction results'));
        break;
      }
    }
  };

  const visible = expanded ? rows : rows.slice(0, 8);
  const half = Math.ceil(visible.length / 2) || 0;
  const columns = [visible.slice(0, half), visible.slice(half)].filter((column) => column.length);
  const canExpand = !expanded && (total > visible.length || hasMore);

  return (
    <section className="games-detail-panel">
      <header className="games-panel-head">
        <div>
          <strong>Prediction results</strong>
          <span>{formatFanCount(total)}</span>
        </div>
        {canExpand ? (
          <button type="button" onClick={viewAll}>View all</button>
        ) : null}
      </header>
      {loading && !rows.length ? <p className="games-panel-empty">Loading predictions...</p> : null}
      {error ? <p className="games-dialog-error">{error}</p> : null}
      {columns.length ? (
        <div className="games-rank-cols">
          {columns.map((column) => (
            <div key={column[0].rank}>
              {column.map((row) => (
                <div key={row.user_id || row.rank} className={row.is_you ? 'games-rank is-you' : 'games-rank'}>
                  <RankMark rank={row.rank} />
                  <span className="games-rank-avatar">
                    {normalizeMediaUrl(row.avatar_url) ? (
                      <img src={normalizeMediaUrl(row.avatar_url)} alt="" />
                    ) : (
                      <PersonMark />
                    )}
                  </span>
                  <strong>{row.is_you ? 'You' : row.display_name || row.name || 'User'}</strong>
                  <em>{predictionScore(row)}</em>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
      {!loading && !error && !rows.length ? <p className="games-panel-empty">No predictions yet</p> : null}
    </section>
  );
}

function GameDetail({ event, onClose }) {
  const scored = hasFinalScore(event);
  const checked = !!event.already_checked_in;
  const predicted = !!(event.already_predicted || readPrediction(event));
  const predictedText = predictionLabel(event);

  return (
    <div className="games-detail">
      <div className="games-detail-bar">
        <button type="button" className="games-back" onClick={onClose}>
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M10 3.5 5.5 8 10 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          All games
        </button>
      </div>
      <section className="games-detail-score">
        <div className="games-scoreboard">
          <TeamMark name={event.opponent_name} logoUrl={event.opponent_logo} />
          <div className="games-final">
            {scored ? (
              <span className="games-final-nums">
                <strong>{event.away_score}</strong>
                <em>Final</em>
                <strong>{event.home_score}</strong>
              </span>
            ) : (
              <strong className="games-final-time">{formatEventTime(event.time)}</strong>
            )}
            <span>{eventMeta(event)}</span>
          </div>
          <TeamMark name={event.home_name} logoUrl={event.home_logo} />
        </div>
        <div className="games-detail-status">
          <span>
            {checked ? <CheckMark /> : <EmptyBox />}
            {checked ? 'Checked in' : 'Not checked in'}
          </span>
          <i aria-hidden="true">·</i>
          <span>
            <PredictMark active={predicted} />
            {predicted && predictedText ? `Predicted ${predictedText}` : 'Not predicted'}
          </span>
        </div>
      </section>
      {scored ? <PredictionBoard event={event} /> : null}
    </div>
  );
}

function GameCard({ event, onPredict, onCheckIn, onShare, onOpen }) {
  const checked = !!event.already_checked_in;
  const predicted = !!(event.already_predicted || readPrediction(event));
  const scored = hasFinalScore(event);
  const locked = !scored && !predicted && !isPredictionOpen(event);
  const both = checked && predicted;
  const showPredict = !both && (!predicted || scored);
  const showCheck = !both && !checked;
  const predictText = scored ? 'Prediction results' : 'Predict the score';
  const predictedText = predictionLabel(event);

  const openFromCard = (click) => {
    click.stopPropagation();
    if (scored) onOpen(event);
    else onPredict(event);
  };

  return (
    <article className="games-card" onClick={() => onOpen(event)}>
      <div className="games-match">
        <TeamMark name={event.opponent_name} logoUrl={event.opponent_logo} />
        <span className="games-when">
          <strong>{formatEventTime(event.time)}</strong>
          <em>{`${event.is_home ? 'Home' : 'Away'} · ${formatEventDate(event.date)}`}</em>
        </span>
        <TeamMark name={event.home_name} logoUrl={event.home_logo} />
      </div>
      <div className="games-status">
        <span className="games-status-main">
          {(!predicted || checked) ? (
            <span>
              {checked ? <CheckMark /> : <EmptyBox />}
              {checked ? 'Checked in' : 'Not checked in'}
            </span>
          ) : null}
          {!checked && !predicted ? <i aria-hidden="true">·</i> : null}
          {checked && predicted ? <i aria-hidden="true">·</i> : null}
          {(!checked || predicted) ? (
            predicted && predictedText ? (
              <button type="button" onClick={openFromCard}>
                <PredictMark active />
                {`Predicted ${predictedText}`}
              </button>
            ) : (
              <span>
                <PredictMark />
                Not predicted
              </span>
            )
          ) : null}
        </span>
        {(checked !== predicted) ? (
          <button type="button" className="games-share-icon" aria-label="Share" onClick={(click) => { click.stopPropagation(); onShare(event); }}>
            <ShareMark />
          </button>
        ) : null}
      </div>
      <div className={`games-actions${showPredict && showCheck ? '' : ' is-single'}`}>
        {both ? (
          <button type="button" className="games-btn is-primary" onClick={(click) => { click.stopPropagation(); onShare(event); }}>
            Share
          </button>
        ) : (
          <>
            {showPredict ? (
              <button
                type="button"
                className={`games-btn ${checked ? 'is-primary' : 'is-muted'}`}
                onClick={openFromCard}
                disabled={locked}
              >
                {predictText}
              </button>
            ) : null}
            {showCheck ? (
              <button type="button" className="games-btn is-primary" onClick={(click) => { click.stopPropagation(); onCheckIn(event); }}>
                Check in
              </button>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
}

function GamesPage() {
  const { user, setUser } = useShellUser();
  const [tab, setTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predictEvent, setPredictEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [checkIn, setCheckIn] = useState(null);
  const [checkInError, setCheckInError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [awarded, setAwarded] = useState(0);
  const [locateTask, setLocateTask] = useState(null);

  const load = useCallback(async () => {
    const current = await apiService.fetchCurrentUser().catch(() => apiService.getCurrentUser());
    if (current) setUser(current);
    const schoolId = schoolIdOf(current);
    if (!schoolId) {
      setEvents([]);
      return;
    }
    const schedules = await apiService.getSportSchedules(schoolId);
    const bundles = await Promise.all(
      schedules.map(async (sport) => ({
        id: sport.id,
        events: await apiService.getScheduleEvents(sport.id),
      }))
    );
    const byId = {};
    bundles.forEach((bundle) => {
      byId[bundle.id] = bundle.events;
    });
    setEvents(flattenScheduleEvents(schedules, byId, current));
  }, [setUser]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load()
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const today = todayStamp();
  const filtered = events.filter((event) => {
    const past = isPastEvent(event, today);
    return tab === 'past' ? past : !past;
  });

  const openPredict = (event) => {
    if (hasFinalScore(event)) {
      setDetailEvent(event);
      return;
    }
    const hasPrediction = !!(event.already_predicted || event.my_prediction);
    if (!hasPrediction && !isPredictionOpen(event)) return;
    setPredictEvent(event);
  };

  const openCheckIn = (event) => {
    setCheckInError('');
    setAwarded(0);
    if (!event.check_in_enabled) {
      setCheckIn({ event, mode: 'disabled' });
      return;
    }
    if (event.already_checked_in || isWithinCheckInWindow(event)) {
      setLocateTask(readPosition());
      setCheckIn({ event, mode: 'map' });
      return;
    }
    setCheckIn({ event, mode: 'window' });
  };

  const submitCheckIn = async (coords) => {
    const event = checkIn?.event;
    if (!event || submitting || event.already_checked_in) return;
    setSubmitting(true);
    setCheckInError('');
    try {
      const position = coords || (await readPosition());
      if (!position || position.latitude == null || position.longitude == null) {
        setCheckInError('Could not get GPS. Allow location access and try again.');
        return;
      }
      if (event.latitude == null || event.longitude == null) {
        setCheckInError('This game has no check-in pin yet.');
        return;
      }
      if (!isWithinCheckInWindow(event)) {
        const opensAt = checkInOpensMs(event);
        const early = opensAt != null && Date.now() < opensAt;
        setCheckInError(
          early ? 'Check-in opens 15 minutes before the game.' : 'Check-in is closed for this game.'
        );
        return;
      }
      const response = await apiService.createScheduleEventCheckIn(event.id, position);
      const points = response?.points_awarded != null
        ? response.points_awarded
        : event.check_in_points || 500;
      setEvents((prev) => prev.map((row) => (
        String(row.id) === String(event.id) ? { ...row, already_checked_in: true } : row
      )));
      setUser((prev) => (
        prev
          ? { ...prev, current_points: (Number(prev.current_points) || 0) + (Number(points) || 0) }
          : prev
      ));
      setAwarded(Number(points) || 0);
      setCheckIn({ event: { ...event, already_checked_in: true }, mode: 'success' });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.already_checked_in) {
        setEvents((prev) => prev.map((row) => (
          String(row.id) === String(event.id) ? { ...row, already_checked_in: true } : row
        )));
        setCheckIn((prev) => (prev ? { ...prev, event: { ...prev.event, already_checked_in: true } } : prev));
        setCheckInError(
          (Array.isArray(data.already_checked_in) && data.already_checked_in[0]) ||
            "You're already checked in"
        );
        return;
      }
      if (data?.too_early) {
        setCheckInError(
          (Array.isArray(data.too_early) && data.too_early[0]) ||
            'Check-in opens 15 minutes before the game.'
        );
        return;
      }
      setCheckInError(apiErrorMessage(err, 'Check-in failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (checkIn && checkIn.mode !== 'disabled') {
    return (
      <CheckInScreen
        event={checkIn.event}
        mode={checkIn.mode}
        user={user}
        error={checkInError}
        awarded={awarded}
        submitting={submitting}
        locateTask={locateTask}
        onClose={() => setCheckIn(null)}
        onSubmit={submitCheckIn}
      />
    );
  }

  if (detailEvent) {
    const current = events.find((row) => String(row.id) === String(detailEvent.id)) || detailEvent;
    return (
      <GameDetail
        event={current}
        onClose={() => setDetailEvent(null)}
      />
    );
  }

  return (
    <div className="games-page">
      <div className="games-toolbar">
        <div className="games-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'upcoming'} className={tab === 'upcoming' ? 'is-on' : ''} onClick={() => setTab('upcoming')}>
            Upcoming
          </button>
          <button type="button" role="tab" aria-selected={tab === 'past'} className={tab === 'past' ? 'is-on' : ''} onClick={() => setTab('past')}>
            Past
          </button>
        </div>
      </div>
      {loading ? <p className="games-empty">Loading games...</p> : null}
      {!loading && !filtered.length ? (
        <p className="games-empty">{tab === 'past' ? 'No past games' : 'No upcoming games'}</p>
      ) : null}
      {!loading && filtered.length ? (
        <div className="games-grid">
          {filtered.map((event) => (
            <GameCard
              key={String(event.id)}
              event={event}
              onPredict={openPredict}
              onCheckIn={openCheckIn}
              onShare={shareGame}
              onOpen={setDetailEvent}
            />
          ))}
        </div>
      ) : null}
      {predictEvent ? (
        <PredictDialog
          event={predictEvent}
          onClose={() => setPredictEvent(null)}
          onSaved={(saved) => {
            if (!saved?.id) return;
            setEvents((prev) => prev.map((row) => (
              String(row.id) === String(saved.id) ? { ...row, ...saved } : row
            )));
          }}
        />
      ) : null}
      {checkIn?.mode === 'disabled' ? (
        <div className="games-modal" role="presentation" onClick={() => setCheckIn(null)}>
          <div className="games-dialog" role="dialog" aria-modal="true" aria-label="Check in" onClick={(e) => e.stopPropagation()}>
            <h2>Check in</h2>
            <p className="games-dialog-note">Check-in is disabled for this game</p>
            <p className="games-dialog-copy">This game does not have check-in enabled.</p>
            <div className="games-dialog-actions">
              <button type="button" className="games-btn is-primary" onClick={() => setCheckIn(null)}>
                Back to games
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function GamesGate() {
  if (!apiService.isAuthenticated()) return <TempStart />;
  return (
    <AppShell title="Games">
      <GamesPage />
    </AppShell>
  );
}

export default GamesGate;
